// src/lib/cache/calendarEvents.js
//
// Per-entity cache wiring for calendar_events. Pilot entity for the
// mega-query migration. The underlying api functions in api/communication/
// calendar.js map start_time/end_time to start/end Date objects on read; the
// hooks below preserve that mapping in cache (no query persister is in use,
// so Date objects are safe to cache).

import {
  getCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
} from '../api/communication/calendar.js'
import {
  useEntityListQuery,
  useOptimisticListMutation,
  useStandardDeleteMutation,
  unwrapList,
} from './entityCache.js'

const ENTITY = 'calendar_events'

// Mirror the date-mapping that getCalendarEvents performs after a read so
// optimistic rows render identically to fetched ones.
const withDateFields = (row) => ({
  ...row,
  start: row.start_time ? new Date(row.start_time) : row.start,
  end: row.end_time ? new Date(row.end_time) : row.end,
})

export const useCalendarEventsQuery = () =>
  useEntityListQuery(ENTITY, unwrapList('calendar events', getCalendarEvents))

export const useCreateCalendarEventMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: (eventData) => createCalendarEvent(eventData),
    applyOptimistic: (prev, vars, { tempId }) => [withDateFields({
      id: tempId,
      ...vars,
      start_time: vars.start_time || vars.start,
      end_time: vars.end_time || vars.end,
    }), ...prev],
    reconcile: (prev, serverRow, { tempId }) => {
      const next = withDateFields(serverRow)
      const replaced = prev.map((row) => (row.id === tempId ? next : row))
      return replaced.some((row) => row.id === next.id)
        ? replaced
        : [next, ...replaced]
    },
  })

export const useUpdateCalendarEventMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: ({ id, ...patch }) => updateCalendarEvent(id, patch),
    applyOptimistic: (prev, vars) =>
      prev.map((row) =>
        row.id === vars.id ? withDateFields({ ...row, ...vars }) : row
      ),
    reconcile: (prev, serverRow) => {
      const next = withDateFields(serverRow)
      return prev.map((row) => (row.id === next.id ? next : row))
    },
  })

export const useDeleteCalendarEventMutation = () =>
  useStandardDeleteMutation(ENTITY, deleteCalendarEvent)
